import { useState } from 'react';
import { useQuery } from 'react-query';
import { getCountries } from 'react-phone-number-input';
import countryLabels from 'react-phone-number-input/locale/fr.json';
import isISO31661Alpha2 from 'validator/es/lib/isISO31661Alpha2';

import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useDebounce } from '@/hooks/useDebounce';
import {
  formatPatientAddress,
  isFrenchPostalCode,
} from '@aldebaran/common/util/patientDataCollection';
import {
  GEOCODING_MIN_QUERY_LENGTH,
  searchAddresses,
  type AddressSuggestion,
} from '@/util/geocoding';

import { AutocompleteInput } from './AutocompleteInput';
import { FieldHelper, FieldLabel } from './FieldHelper';

export type AddressValue = {
  addressStreet: string | null;
  addressPostalCode: string | null;
  addressCity: string | null;
  addressCountry: string | null;
};

export const EMPTY_ADDRESS: AddressValue = {
  addressStreet: null,
  addressPostalCode: null,
  addressCity: null,
  addressCountry: null,
};

// The phone library also lists calling-code regions that are not ISO 3166-1
// countries (AC, TA, XK), which the API's @IsISO31661Alpha2 rejects.
export const COUNTRY_OPTIONS = getCountries()
  .filter((code) => isISO31661Alpha2(code))
  .map((code) => ({
    code,
    label: (countryLabels as Record<string, string>)[code] ?? code,
  }))
  .sort((a, b) =>
    a.code === 'FR' ? -1 : b.code === 'FR' ? 1 : a.label.localeCompare(b.label),
  );

const isFrench = (country: string | null) => !country || country === 'FR';

export const isAddressEmpty = (value: AddressValue) =>
  !value.addressStreet && !value.addressPostalCode && !value.addressCity;

/**
 * French error message for an address, or undefined when it is empty or
 * complete. Street, postal code and city go together; the postal code must be
 * 5 digits for a French address (`isFrenchPostalCode`, shared with the API).
 */
export const getAddressError = (value: AddressValue) => {
  if (isAddressEmpty(value)) return undefined;
  if (!value.addressStreet || !value.addressPostalCode || !value.addressCity)
    return 'Adresse incomplète : renseignez la rue, le code postal et la ville';
  if (
    isFrench(value.addressCountry) &&
    !isFrenchPostalCode(value.addressPostalCode)
  )
    return 'Le code postal doit contenir 5 chiffres';
  return undefined;
};

type AddressAutocompleteFieldProps = {
  value: AddressValue;
  onChange: (value: AddressValue) => void;
  label?: string;
  // Parent-driven error, e.g. "required" on submit. Takes precedence.
  error?: string;
  optional?: boolean;
  // False when someone fills the form for the patient (interlocuter OTHER).
  isSelf?: boolean;
  // Reports the field's own error (e.g. typed text not picked from the list),
  // which the parent can't see since that text never reaches `value`.
  onLocalErrorChange?: (error: string | undefined) => void;
};

export const AddressAutocompleteField = ({
  value,
  onChange,
  label = 'Adresse postale',
  error,
  optional,
  isSelf = true,
  onLocalErrorChange,
}: AddressAutocompleteFieldProps) => {
  // A known foreign address can only be edited manually.
  const [manual, setManual] = useState(!isFrench(value.addressCountry));
  const [query, setQuery] = useState(formatPatientAddress(value) ?? '');
  const [touchedError, setTouchedErrorState] = useState<string>();
  const setTouchedError = (next: string | undefined) => {
    setTouchedErrorState(next);
    onLocalErrorChange?.(next);
  };
  const debouncedQuery = useDebounce(query.trim(), 300);

  const hasSelection = !isAddressEmpty(value);
  const isSearchable =
    !manual &&
    !hasSelection &&
    debouncedQuery.length >= GEOCODING_MIN_QUERY_LENGTH;

  const {
    data: suggestions = [],
    isFetching,
    isError,
    isSuccess,
  } = useQuery(
    ['geocoding', 'address', debouncedQuery],
    ({ signal }) => searchAddresses(debouncedQuery, signal),
    { enabled: isSearchable, staleTime: Infinity, retry: 1 },
  );

  const displayedError = error ?? touchedError;

  const updateManual = (patch: Partial<AddressValue>) => {
    setTouchedError(undefined);
    onChange({
      ...value,
      addressCountry: value.addressCountry ?? 'FR',
      ...patch,
    });
  };

  return (
    <div className="flex flex-col gap-1">
      <FieldLabel optional={optional}>{label}</FieldLabel>

      {manual ? (
        <div className="flex flex-col gap-2">
          <Input
            aria-label="Numéro et rue"
            placeholder="Numéro et rue"
            autoComplete="address-line1"
            value={value.addressStreet ?? ''}
            aria-invalid={!!displayedError || undefined}
            onChange={(event) =>
              updateManual({ addressStreet: event.target.value || null })
            }
          />
          <Input
            aria-label="Code postal"
            placeholder="Code postal"
            autoComplete="postal-code"
            inputMode={isFrench(value.addressCountry) ? 'numeric' : 'text'}
            value={value.addressPostalCode ?? ''}
            aria-invalid={!!displayedError || undefined}
            onChange={(event) =>
              updateManual({ addressPostalCode: event.target.value || null })
            }
          />
          <Input
            aria-label="Ville"
            placeholder="Ville"
            autoComplete="address-level2"
            value={value.addressCity ?? ''}
            aria-invalid={!!displayedError || undefined}
            onChange={(event) =>
              updateManual({ addressCity: event.target.value || null })
            }
            onBlur={() => setTouchedError(getAddressError(value))}
          />
          <Select
            aria-label="Pays"
            autoComplete="country"
            value={value.addressCountry ?? 'FR'}
            onChange={(event) =>
              updateManual({ addressCountry: event.target.value })
            }
          >
            {COUNTRY_OPTIONS.map(({ code, label: countryLabel }) => (
              <option key={code} value={code}>
                {countryLabel}
              </option>
            ))}
          </Select>
        </div>
      ) : (
        <AutocompleteInput<AddressSuggestion>
          ariaLabel={label}
          value={query}
          placeholder={'5 Parv. Alan Turing Station F, 75013 Paris'}
          onValueChange={(next) => {
            setQuery(next);
            setTouchedError(undefined);
            // Typing again invalidates the previously selected address.
            if (hasSelection) onChange(EMPTY_ADDRESS);
          }}
          suggestions={isSearchable ? suggestions : []}
          getSuggestionKey={(address) => address.label}
          renderSuggestion={(address) => address.label}
          onSelect={(address) => {
            const selected: AddressValue = {
              addressStreet: address.street,
              addressPostalCode: address.postalCode,
              addressCity: address.city,
              addressCountry: 'FR',
            };
            setQuery(formatPatientAddress(selected) ?? address.label);
            setTouchedError(undefined);
            onChange(selected);
          }}
          isLoading={isFetching}
          showEmptyMessage={isSearchable && isSuccess && !isFetching}
          emptyMessage="Aucune adresse trouvée"
          invalid={!!displayedError}
          onBlur={() =>
            setTouchedError(
              query.trim() && !hasSelection
                ? isSelf
                  ? 'Sélectionnez votre adresse dans la liste ou saisissez-la manuellement'
                  : 'Sélectionnez l’adresse dans la liste ou saisissez-la manuellement'
                : undefined,
            )
          }
        />
      )}

      <FieldHelper error={!!displayedError || (isError && !manual)}>
        {displayedError ??
          (isError && !manual
            ? 'La recherche d’adresse est indisponible, saisissez-la manuellement.'
            : undefined)}
      </FieldHelper>

      <button
        type="button"
        className="w-fit cursor-pointer text-xs font-medium text-indigo-900 underline"
        onClick={() => {
          setTouchedError(undefined);
          if (manual) {
            // Back to search: start again from the address as typed.
            setQuery('');
            onChange(EMPTY_ADDRESS);
          } else {
            // Keep whatever was selected so it can be completed by hand.
            onChange({
              ...value,
              addressCountry: value.addressCountry ?? 'FR',
            });
          }
          setManual(!manual);
        }}
      >
        {manual
          ? isSelf
            ? 'Rechercher mon adresse'
            : 'Rechercher l’adresse'
          : 'Saisir manuellement'}
      </button>
    </div>
  );
};
